/* eslint-disable no-restricted-syntax */
/* eslint-disable no-undef */
import { AUTHORITIES } from 'domain/fuse/auth/constants';
import React from 'react';
import { Loadable } from 'circe/loadable/provider';
import loadable from '@loadable/component';
import { NOTIFICATIONS } from 'domain/layout/constants';
import LoadingRoute from './components/LoadingIndicator';

export const isAllowedMenuItem = (key, authorities = []) => {
  const MENU_ITEMS = {
    PULSE: [AUTHORITIES.ACCOUNT_ADMIN, AUTHORITIES.SYSTEM_ADMIN],
    SHIPPERS: [
      AUTHORITIES.ACCOUNT_ADMIN,
      AUTHORITIES.SYSTEM_ADMIN,
      AUTHORITIES.OPERATOR,
      AUTHORITIES.DOMAIN_ADMIN,
    ],
    SHIPMENTS: [AUTHORITIES.ACCOUNT_ADMIN, AUTHORITIES.SYSTEM_ADMIN, AUTHORITIES.OPERATOR],
    ORDERS: [
      AUTHORITIES.ACCOUNT_ADMIN,
      AUTHORITIES.SYSTEM_ADMIN,
      AUTHORITIES.OPERATOR,
      AUTHORITIES.CALL_CENTER_ASSOCIATE,
    ],

    UPDATE_STATUS: [AUTHORITIES.ACCOUNT_ADMIN, AUTHORITIES.FINANCE_ADMIN],
    DRIVERS_PAGE: [
      AUTHORITIES.ACCOUNT_ADMIN,
      AUTHORITIES.SYSTEM_ADMIN,
      AUTHORITIES.OPERATOR,
      AUTHORITIES.FLEET_ADMIN,
    ],
    DRIVERS: [
      AUTHORITIES.ACCOUNT_ADMIN,
      AUTHORITIES.SYSTEM_ADMIN,
      AUTHORITIES.OPERATOR,
      AUTHORITIES.FLEET_ADMIN,
      AUTHORITIES.CALL_CENTER_ASSOCIATE,
    ],
    CARRIERS: [AUTHORITIES.ACCOUNT_ADMIN, AUTHORITIES.ACCOUNT_ADMIN_COURIER],
    DRIVERS_TRACKING: [AUTHORITIES.ACCOUNT_ADMIN, AUTHORITIES.SYSTEM_ADMIN, AUTHORITIES.OPERATOR],
    MY_SHIPMENTS: [
      AUTHORITIES.ACCOUNT_ADMIN,
      AUTHORITIES.SYSTEM_ADMIN,
      AUTHORITIES.OPERATOR,
      AUTHORITIES.DOMAIN_ADMIN,
      AUTHORITIES.SHIPPER,
      AUTHORITIES.FLEET_ADMIN,
      AUTHORITIES.FINANCE_ADMIN,
    ],
    SHIPPING: [AUTHORITIES.AUTHENTICATED],
    SUPPORT: [AUTHORITIES.CALL_CENTER_ASSOCIATE],
    CREDIT_TERMS: [AUTHORITIES.FINANCE_ADMIN],
    OPERATIONS_DASHBOARD: [
      AUTHORITIES.OPERATOR,
      AUTHORITIES.FLEET_ADMIN,
      AUTHORITIES.ACCOUNT_ADMIN,
    ],
    SHIPMENT_ALERTS: [AUTHORITIES.ACCOUNT_ADMIN],
    FINANCIAL_DASHBOARD: [AUTHORITIES.FINANCE_ADMIN],
    GENERATE_PAYABLE: [AUTHORITIES.FINANCE_ADMIN, AUTHORITIES.ACCOUNT_ADMIN, AUTHORITIES.OPERATOR],
    BATCH: [AUTHORITIES.ACCOUNT_ADMIN, AUTHORITIES.SHIPPER, AUTHORITIES.OPERATOR],
    MANIFEST: [AUTHORITIES.ACCOUNT_ADMIN, AUTHORITIES.OPERATOR, AUTHORITIES.SHIPPER],
    SEARCH: [
      AUTHORITIES.ACCOUNT_ADMIN,
      AUTHORITIES.SYSTEM_ADMIN,
      AUTHORITIES.OPERATOR,
      AUTHORITIES.DOMAIN_ADMIN,
      AUTHORITIES.SHIPPER,
      AUTHORITIES.FLEET_ADMIN,
      AUTHORITIES.FINANCE_ADMIN,
    ],
    SETTINGS: [
      AUTHORITIES.ACCOUNT_ADMIN,
      AUTHORITIES.SYSTEM_ADMIN,
      AUTHORITIES.OPERATOR,
      AUTHORITIES.DOMAIN_ADMIN,
      AUTHORITIES.SHIPPER,
      AUTHORITIES.FLEET_ADMIN,
      AUTHORITIES.FINANCE_ADMIN,
    ],
    USERS: [AUTHORITIES.ACCOUNT_ADMIN, AUTHORITIES.ACCOUNT_ADMIN_COURIER],
    TRIPS: [
      AUTHORITIES.ACCOUNT_ADMIN,
      AUTHORITIES.SYSTEM_ADMIN,
      AUTHORITIES.OPERATOR,
      AUTHORITIES.ACCOUNT_ADMIN_COURIER,
    ],
    TRIPS_FINANCE: [
      AUTHORITIES.ACCOUNT_ADMIN,
      AUTHORITIES.SYSTEM_ADMIN,
      AUTHORITIES.OPERATOR,
      AUTHORITIES.ACCOUNT_ADMIN_COURIER,
      AUTHORITIES.FINANCE_ADMIN,
      AUTHORITIES.CALL_CENTER_ASSOCIATE,
    ],
    VEHICLES: [
      AUTHORITIES.ACCOUNT_ADMIN,
      AUTHORITIES.SYSTEM_ADMIN,
      AUTHORITIES.OPERATOR,
      AUTHORITIES.FLEET_ADMIN,
    ],
  };

  const perms = MENU_ITEMS[key] || [];

  return authorities.filter((value) => perms.indexOf(value) !== -1).length > 0;
};

/**
 * Loading Component
 * @param importCallback
 * @returns {*}
 */
export const loadComponent = (importCallback) => loadable(() => importCallback(), {
  fallback: <LoadingRoute />,
});

export const truncateNotification = (content, maxLength) => (content.length > maxLength ? `${content.substring(0, maxLength)}...` : content);

export const isNotificationTruncated = (content, maxLength) => content.length > maxLength;

export const filterAndMergeNotifications = (
  existingNotifications,
  newNotifications,
  closedNotifications,
) => {
  const newValidNotifications = newNotifications.filter(
    (newNotification) => !existingNotifications.some(
      (existingNotification) => existingNotification.id === newNotification.id,
    ) && !closedNotifications.includes(newNotification.id),
  );

  return [...existingNotifications, ...newValidNotifications];
};

export const addDefaultValuesToNotifications = (newNotifications, allNotifications) => {
  const notificationsWithDefaults = newNotifications
    .filter((notification) => notification.id)
    .map((notification) => {
      const existingNotification = allNotifications.find(
        (existing) => existing.id === notification.id,
      );

      if (!existingNotification) {
        return {
          ...notification,
          createdAt: new Date(),
          status: notification.status || NOTIFICATIONS.status.unread,
        };
      }

      return {
        ...existingNotification,
      };
    });

  return notificationsWithDefaults;
};

export const removeDuplicateNotifications = (notifications) => {
  const uniqueNotifications = [];
  const seenIds = [];

  for (const notification of notifications) {
    if (!seenIds.includes(notification.id)) {
      seenIds.push(notification.id);
      uniqueNotifications.push(notification);
    }
  }

  return uniqueNotifications;
};

export const storeNotifications = (newNotifications) => {
  const existingNotifications = JSON.parse(localStorage.getItem(NOTIFICATIONS.key));

  if (existingNotifications) {
    const updatedNotifications = removeDuplicateNotifications([
      ...newNotifications,
      ...existingNotifications,
    ]);

    if (updatedNotifications.length > 20) {
      updatedNotifications.sort((a, b) => b.createdAt - a.createdAt);
      updatedNotifications.splice(20);
    }

    localStorage.setItem(NOTIFICATIONS.key, JSON.stringify(updatedNotifications));
  } else {
    const uniqueNewNotifications = removeDuplicateNotifications(newNotifications);
    localStorage.setItem(NOTIFICATIONS.key, JSON.stringify(uniqueNewNotifications));
  }
};

export const getNotifications = () => {
  const existingNotifications = JSON.parse(localStorage.getItem(NOTIFICATIONS.key)) || [];

  const sortedNotifications = existingNotifications.sort(
    (a, b) => new Date(b.createdAt) - new Date(a.createdAt),
  );
  return sortedNotifications;
};

export const markNotificationAsRead = (id) => {
  const existingNotifications = getNotifications();

  const updatedNotifications = existingNotifications.map((notification) => {
    if (notification.id === id) {
      return { ...notification, status: NOTIFICATIONS.status.read };
    }
    return notification;
  });
  localStorage.setItem(NOTIFICATIONS.key, JSON.stringify(updatedNotifications));
  return removeDuplicateNotifications(updatedNotifications);
};

export const countUnreadNotifications = () => {
  const existingNotifications = getNotifications();

  const unreadCount = existingNotifications.reduce((count, notification) => {
    if (notification.status === NOTIFICATIONS.status.unread) {
      return count + 1;
    }
    return count;
  }, 0);

  return unreadCount;
};
